/**
 * APEX v8.14 — normalizeGame
 *
 * Normalise un objet game pour garantir un format unique côté frontend.
 *
 * Pourquoi : historiquement, les games coexistent avec deux conventions :
 *   - Anciennes : `cs_per_min`, `queue_id`, `enemy_adc` (snake_case DB-like)
 *   - Nouvelles (import Riot) : `csPerMin`, `queueId`, `enemyAdc` (camelCase JS)
 *
 * Le code avait des `g.csPerMin || g.cs_per_min` partout, ce qui était
 * fragile et facile à oublier. Désormais, toutes les games passent par
 * `normalizeGame()` dans `gamesDB.list()` — le reste du code peut utiliser
 * les noms camelCase canoniques sans se soucier de la source.
 *
 * Convention retenue : **camelCase** pour les champs de gameplay,
 * **snake_case** uniquement pour les colonnes brutes Supabase qui ne
 * sont jamais lues directement par l'UI (`created_at`, `updated_at`, etc.)
 */

const FIELD_ALIASES = {
  // camelCase canonique : [aliases potentiels]
  csPerMin:        ['cs_per_min'],
  queueId:         ['queue_id'],
  enemyAdc:        ['enemy_adc', 'opponent_adc', 'opponentAdc'],
  enemySupport:    ['enemy_support', 'opponent_support', 'opponentSupport'],
  enemyMid:        ['enemy_mid', 'opponent_mid', 'opponentMid'],
  enemyTop:        ['enemy_top', 'opponent_top', 'opponentTop'],
  allyAdc:         ['ally_adc'],
  allyTop:         ['ally_top'],
  durationSeconds: ['duration_seconds'],
  startTime:       ['start_time', 'gameStartTimestamp', 'gameCreation'],
  deathDetails:    ['death_details'],
  isAutofill:      ['is_autofill'],
};

/**
 * Normalise un game en garantissant les champs camelCase canoniques.
 * - Pour chaque alias trouvé, copie la valeur sur le champ canonique
 *   (sans toucher à l'original, pour compat éventuelle ancienne)
 * - Ajoute aussi `timestamp` (alias unifié sur `startTime` ou `created_at`)
 *
 * @param {object} game — game lue de la DB ou créée à l'import Riot
 * @returns {object} game enrichie avec les champs canoniques
 */
export function normalizeGame(game) {
  if (!game || typeof game !== 'object') return game;
  const normalized = { ...game };

  for (const [canonical, aliases] of Object.entries(FIELD_ALIASES)) {
    if (normalized[canonical] != null) continue; // déjà présent en camelCase
    for (const alias of aliases) {
      if (game[alias] != null) {
        normalized[canonical] = game[alias];
        break;
      }
    }
  }

  // timestamp unifié : si pas déjà présent, dérive de startTime ou created_at
  if (normalized.timestamp == null) {
    normalized.timestamp = normalized.startTime || normalized.created_at || null;
  }

  return normalized;
}
